import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPrint,
  faDownload,
  faSearch,
  faFilter,
  faEdit,
  faEye,
  faFileExport,
  faFilePdf,
  faFileExcel,
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faChartLine,
  faUserGraduate,
  faSchool,
  faCalendarAlt,
  faUser,
  faIdCard,
  faBook,
  faGraduationCap,
  faAward,
  faPercentage,
  faSort,
  faSortUp,
  faSortDown,
  faChevronDown,
  faChevronUp,
  faRefresh,
  faCog,
  faInfoCircle,
  faCalculator,
  faClipboardList,
  faChartBar,
  faPencilAlt,
  faBookOpen,
  faRunning,
  faPalette,
  faMusic,
  faUsers,
  faCalendarDay,
  faSignature,
  faFlask,
  faFileSignature,
  faCheckSquare,
  faTimesCircle,
  faComment,
  faCheck,
  faTimes as faTimesIcon,
  faMinus
} from "@fortawesome/free-solid-svg-icons";
import "./sampleRC.css";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const ReportCardTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for filters
  const [filters, setFilters] = useState({
    academicYear: "2024-2025",
    term: "All Terms",
    classFilter: "",
    sectionFilter: "",
    rollNumber: "",
    searchQuery: ""
  });

  // State for data - Updated to match template
  const [reportCardData, setReportCardData] = useState({
    schoolName: "BRIDGE HIGH SCHOOL",
    schoolAddress: "123 Education Street, Academic City, AC 12345",
    studentName: "John Alexander Smith",
    school: "BRIDGE HIGH SCHOOL",
    teacherName: "Ms. Jennifer Wilson",
    sessions: "Fall 2024",
    
    evaluation: [
      { trait: "Diligent", value: true },
      { trait: "Debate", value: true },
      { trait: "Friendly", value: true },
      { trait: "Proactive to teacher", value: true },
      { trait: "Responsible", value: true }
    ],
    
    subjects: [
      { id: 1, name: "Math", grade: "A", remarks: "Excellent performance" },
      { id: 2, name: "English", grade: "B", remarks: "Good progress" },
      { id: 3, name: "Economics", grade: "A", remarks: "Outstanding" },
      { id: 4, name: "Biology", grade: "A", remarks: "Excellent work" },
      { id: 5, name: "Chemistry", grade: "B", remarks: "Good effort" },
      { id: 6, name: "History", grade: "A", remarks: "Excellent understanding" }
    ],
    
    metrics: [
      { grade: "A", description: "Excellent" },
      { grade: "B", description: "Great" },
      { grade: "C", description: "Average" },
      { grade: "D", description: "Below Average" }
    ],
    
    overallRemarks: "John has shown exceptional growth this semester. His analytical skills in Math and Economics are outstanding, and he actively participates in class discussions. He maintains excellent relationships with peers and teachers. Keep up the great work!",
    
    overallGrade: "A",
    
    attendance: {
      totalDays: "90",
      attended: "88",
      absent: "2"
    }
  });

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Initialize editedData when reportCardData changes
  useEffect(() => {
    setEditedData(reportCardData);
  }, [reportCardData]);

  // Fetch students list
  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      // Mock data for demo
      const mockStudents = [
        { id: 1, name: "John Alexander Smith", rollNumber: "2024-001", class: "Grade 11", section: "A" },
        { id: 2, name: "Emma Wilson", rollNumber: "2024-002", class: "Grade 11", section: "A" },
        { id: 3, name: "Michael Brown", rollNumber: "2024-003", class: "Grade 11", section: "B" },
        { id: 4, name: "Sophia Davis", rollNumber: "2024-004", class: "Grade 10", section: "A" },
        { id: 5, name: "James Miller", rollNumber: "2024-005", class: "Grade 10", section: "B" },
        { id: 6, name: "Olivia Garcia", rollNumber: "2024-006", class: "Grade 11", section: "B" },
        { id: 7, name: "William Martinez", rollNumber: "2024-007", class: "Grade 12", section: "Science" },
        { id: 8, name: "Ava Rodriguez", rollNumber: "2024-008", class: "Grade 11", section: "A" },
        { id: 9, name: "Benjamin Wilson", rollNumber: "2024-009", class: "Grade 10", section: "B" },
        { id: 10, name: "Isabella Taylor", rollNumber: "2024-010", class: "Grade 11", section: "B" }
      ];
      setStudents(mockStudents);
    } catch (error) {
      console.error("Error fetching students:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load student data.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return '#6b7280';
    if (grade === 'A') return '#10b981'; // Green for Excellent
    if (grade === 'B') return '#3b82f6'; // Blue for Great
    if (grade === 'C') return '#f59e0b'; // Orange for Average
    if (grade === 'D') return '#ef4444'; // Red for Below Average
    return '#6b7280';
  };

  const getGradeIcon = (grade) => {
    if (grade === 'A') return faCheckCircle;
    if (grade === 'B') return faCheckCircle;
    if (grade === 'C') return faExclamationTriangle;
    if (grade === 'D') return faTimesIcon;
    return faMinus;
  };

  const generatePDF = () => {
    setLoading(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;
      let yPos = margin;

      // School Header with template style
      doc.setFillColor(59, 89, 152); // Blue color from template
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text("BRIDGE HIGH SCHOOL", pageWidth / 2, 12, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text("123 Education Street, Academic City, AC 12345", pageWidth / 2, 18, { align: 'center' });

      // Student Information
      yPos = 35;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      
      // Draw two columns for student info
      const leftMargin = margin;
      const rightMargin = pageWidth / 2 + 10;
      
      doc.setFont('helvetica', 'bold');
      doc.text("Name:", leftMargin, yPos);
      doc.text("School:", rightMargin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(reportCardData.studentName, leftMargin + 20, yPos);
      doc.text(reportCardData.school, rightMargin + 20, yPos);
      
      yPos += 7;
      doc.setFont('helvetica', 'bold');
      doc.text("Teacher:", leftMargin, yPos);
      doc.text("Sessions:", rightMargin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(reportCardData.teacherName, leftMargin + 25, yPos);
      doc.text(reportCardData.sessions, rightMargin + 25, yPos);

      // Draw horizontal line
      yPos += 10;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, pageWidth - margin, yPos);

      // Evaluation Section
      yPos += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("EVALUATION", pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 10;
      const evalWidth = (pageWidth - 2 * margin) / 5;
      reportCardData.evaluation.forEach((evalItem, index) => {
        const xPos = margin + (index * evalWidth);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(evalItem.trait, xPos + evalWidth/2, yPos, { align: 'center' });
        
        // Draw evaluation indicator
        yPos += 4;
        if (evalItem.value) {
          doc.setFillColor(46, 204, 113); // Green for positive
          doc.circle(xPos + evalWidth/2, yPos, 2, 'F');
        } else {
          doc.setDrawColor(200, 200, 200);
          doc.circle(xPos + evalWidth/2, yPos, 2, 'S');
        }
        yPos -= 4;
      });

      yPos += 10;
      doc.line(margin, yPos, pageWidth - margin, yPos);

      // Subjects Table
      yPos += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("SUBJECT", pageWidth / 2, yPos, { align: 'center' });

      yPos += 10;
      const subjectColumns = ["Subject", "Grade", "Remarks"];
      const subjectRows = reportCardData.subjects.map(subject => [
        subject.name,
        subject.grade,
        subject.remarks || ""
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [subjectColumns],
        body: subjectRows,
        theme: 'grid',
        headStyles: { 
          fillColor: [59, 89, 152],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
          cellPadding: 4
        },
        bodyStyles: { fontSize: 10, cellPadding: 4 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 40 },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 85, fontStyle: 'italic' }
        },
        margin: { left: margin, right: margin },
        styles: { overflow: 'linebreak' }
      });

      // Metrics and Remarks Section - Side by side
      yPos = doc.lastAutoTable.finalY + 10;
      const sectionWidth = (pageWidth - 3 * margin) / 2;

      // Metrics Table
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("METRICS:", margin, yPos);
      
      yPos += 8;
      const metricsRows = reportCardData.metrics.map(metric => [
        metric.grade,
        metric.description
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [["Grade", "Description"]],
        body: metricsRows,
        theme: 'grid',
        headStyles: { 
          fillColor: [52, 152, 219],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 3
        },
        bodyStyles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 25, halign: 'center' },
          1: { cellWidth: 50 }
        },
        margin: { left: margin },
        tableWidth: sectionWidth
      });

      // Remarks Section
      const remarksY = yPos;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("REMARKS", margin * 2 + sectionWidth, remarksY);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const remarksLines = doc.splitTextToSize(reportCardData.overallRemarks, sectionWidth - 10);
      doc.text(remarksLines, margin * 2 + sectionWidth, remarksY + 8);

      // Overall Grade
      const gradeY = remarksY + remarksLines.length * 4 + 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(`GRADE: ${reportCardData.overallGrade}`, margin * 2 + sectionWidth, gradeY);

      // Footer
      const finalY = Math.max(doc.lastAutoTable.finalY, gradeY) + 15;
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text("Official Document - Bridge High School", pageWidth / 2, finalY, { align: 'center' });
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, finalY + 5, { align: 'center' });

      // Save PDF
      doc.save(`Report_Card_${reportCardData.studentName.replace(/\s+/g, '_')}.pdf`);
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'Report card has been downloaded successfully.',
        confirmButtonColor: '#3b5998'
      });

    } catch (error) {
      console.error("PDF Generation Error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Generation Failed',
        text: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      academicYear: "2024-2025",
      term: "All Terms",
      classFilter: "",
      sectionFilter: "",
      rollNumber: "",
      searchQuery: ""
    });
  };

  const refreshData = () => {
    fetchStudents();
    Swal.fire({
      title: "Refreshed!",
      text: "Data has been refreshed",
      icon: "success",
      timer: 1500,
      showConfirmButton: false
    });
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes
      setReportCardData(editedData);
      Swal.fire({
        title: "Saved!",
        text: "Report card updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEvaluationChange = (index, value) => {
    setEditedData(prev => ({
      ...prev,
      evaluation: prev.evaluation.map((item, i) => 
        i === index ? { ...item, value: value === "true" } : item
      )
    }));
  };

  const handleSubjectChange = (id, field, value) => {
    setEditedData(prev => ({
      ...prev,
      subjects: prev.subjects.map(subject => 
        subject.id === id ? { ...subject, [field]: value } : subject
      )
    }));
  };

  const handleMetricChange = (index, field, value) => {
    setEditedData(prev => ({
      ...prev,
      metrics: prev.metrics.map((metric, i) => 
        i === index ? { ...metric, [field]: value } : metric
      )
    }));
  };

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = students.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(students.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading && !reportCardData) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="main-content" style={{ marginLeft: "20%" }}>
          <div className="ReportCardTemplate-loading">
            <div className="ReportCardTemplate-spinner"></div>
            <p>Loading report card data...</p>
          </div>
        </div>
      </div>
    );
  }

  const dataToUse = isEditing ? editedData : reportCardData;

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="main-content" style={{ marginLeft: "20%" }}>
        <section className="content">
          {/* Header Section */}
          <div className="ReportCardTemplate-header">
            <div className="ReportCardTemplate-headerContent">
              <h1 className="ReportCardTemplate-title">
                <FontAwesomeIcon icon={faFileSignature} className="ReportCardTemplate-headerIcon" />
                Report Card Template
              </h1>
              <p className="ReportCardTemplate-subtitle">
                Beautiful report card design with modern layout
              </p>
            </div>
            
            <div className="ReportCardTemplate-headerActions">
              <button 
                className="ReportCardTemplate-btn ReportCardTemplate-btnSecondary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={showFilters ? faTimes : faFilter} />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              
              <button 
                className={`ReportCardTemplate-btn ${isEditing ? 'ReportCardTemplate-btnSuccess' : 'ReportCardTemplate-btnEdit'}`}
                onClick={handleEditToggle}
              >
                <FontAwesomeIcon icon={isEditing ? faCheckSquare : faPencilAlt} />
                {isEditing ? 'Save Changes' : 'Edit Report'}
              </button>
              
              <div className="ReportCardTemplate-dropdown">
                <button className="ReportCardTemplate-btn ReportCardTemplate-btnExport">
                  <FontAwesomeIcon icon={faDownload} /> Export
                </button>
                <div className="ReportCardTemplate-dropdownContent">
                  <a href="#" onClick={(e) => { e.preventDefault(); generatePDF(); }}>
                    <FontAwesomeIcon icon={faFilePdf} /> Export as PDF
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); window.print(); }}>
                    <FontAwesomeIcon icon={faPrint} /> Print Report
                  </a>
                </div>
              </div>
              
              <button 
                className="ReportCardTemplate-btn ReportCardTemplate-btnPrimary"
                onClick={refreshData}
                title="Refresh Data"
              >
                <FontAwesomeIcon icon={faRefresh} /> Refresh
              </button>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className="ReportCardTemplate-filterSection">
              <div className="ReportCardTemplate-filterHeader">
                <h3>
                  <FontAwesomeIcon icon={faSearch} /> Student Search
                </h3>
                <div className="ReportCardTemplate-filterActions">
                  <button 
                    className="ReportCardTemplate-clearFilters"
                    onClick={clearFilters}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Clear All
                  </button>
                  <button 
                    className="ReportCardTemplate-btn ReportCardTemplate-btnApply"
                    onClick={fetchStudents}
                  >
                    Search Students
                  </button>
                </div>
              </div>
              
              <div className="ReportCardTemplate-filterGrid">
                <div className="ReportCardTemplate-filterRow">
                  <div className="ReportCardTemplate-filterGroup">
                    <label className="ReportCardTemplate-filterLabel">
                      <FontAwesomeIcon icon={faGraduationCap} /> Class & Section
                    </label>
                    <div className="ReportCardTemplate-classSectionGrid">
                      <div className="ReportCardTemplate-classSectionGroup">
                        <div className="ReportCardTemplate-classInputWrapper">
                          <input
                            type="text"
                            placeholder="Class (e.g., Grade 11)"
                            value={filters.classFilter}
                            onChange={(e) => setFilters(prev => ({ ...prev, classFilter: e.target.value }))}
                            className="ReportCardTemplate-classInput"
                          />
                        </div>
                      </div>
                      
                      <div className="ReportCardTemplate-classSectionGroup">
                        <div className="ReportCardTemplate-classInputWrapper">
                          <input
                            type="text"
                            placeholder="Section (e.g., A)"
                            value={filters.sectionFilter}
                            onChange={(e) => setFilters(prev => ({ ...prev, sectionFilter: e.target.value }))}
                            className="ReportCardTemplate-classInput"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ReportCardTemplate-filterGroup">
                    <label className="ReportCardTemplate-filterLabel">
                      <FontAwesomeIcon icon={faUser} /> Student Search
                    </label>
                    <div className="ReportCardTemplate-searchBox">
                      <FontAwesomeIcon icon={faSearch} className="ReportCardTemplate-searchIcon" />
                      <input
                        type="text"
                        placeholder="Search by name or roll number..."
                        value={filters.searchQuery}
                        onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                        className="ReportCardTemplate-searchInput"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Report Card - New Design */}
          <div className="ReportCardTemplate-mainContent">
            <div className="ReportCardTemplate-card-new">
              {/* School Header */}
              <div className="ReportCardTemplate-schoolHeader">
                <h1>BRIDGE HIGH SCHOOL</h1>
                <p className="ReportCardTemplate-schoolAddress">
                  {isEditing ? (
                    <input
                      type="text"
                      value={dataToUse.schoolAddress}
                      onChange={(e) => handleInputChange('schoolAddress', e.target.value)}
                      className="ReportCardTemplate-editInput ReportCardTemplate-addressInput"
                    />
                  ) : (
                    dataToUse.schoolAddress
                  )}
                </p>
              </div>

              {/* Student Information */}
              <div className="ReportCardTemplate-studentInfo-new">
                <div className="ReportCardTemplate-info-grid">
                  <div className="ReportCardTemplate-info-item">
                    <span className="ReportCardTemplate-info-label">Name:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={dataToUse.studentName}
                        onChange={(e) => handleInputChange('studentName', e.target.value)}
                        className="ReportCardTemplate-editInput"
                      />
                    ) : (
                      <span className="ReportCardTemplate-info-value">{dataToUse.studentName}</span>
                    )}
                  </div>
                  
                  <div className="ReportCardTemplate-info-item">
                    <span className="ReportCardTemplate-info-label">School:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={dataToUse.school}
                        onChange={(e) => handleInputChange('school', e.target.value)}
                        className="ReportCardTemplate-editInput"
                      />
                    ) : (
                      <span className="ReportCardTemplate-info-value">{dataToUse.school}</span>
                    )}
                  </div>
                  
                  <div className="ReportCardTemplate-info-item">
                    <span className="ReportCardTemplate-info-label">Teacher:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={dataToUse.teacherName}
                        onChange={(e) => handleInputChange('teacherName', e.target.value)}
                        className="ReportCardTemplate-editInput"
                      />
                    ) : (
                      <span className="ReportCardTemplate-info-value">{dataToUse.teacherName}</span>
                    )}
                  </div>
                  
                  <div className="ReportCardTemplate-info-item">
                    <span className="ReportCardTemplate-info-label">Sessions:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={dataToUse.sessions}
                        onChange={(e) => handleInputChange('sessions', e.target.value)}
                        className="ReportCardTemplate-editInput"
                      />
                    ) : (
                      <span className="ReportCardTemplate-info-value">{dataToUse.sessions}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="ReportCardTemplate-divider"></div>

              {/* Evaluation Section */}
              <div className="ReportCardTemplate-evaluation-section">
                <h2>EVALUATION</h2>
                <div className="ReportCardTemplate-evaluation-grid">
                  {dataToUse.evaluation.map((item, index) => (
                    <div key={index} className="ReportCardTemplate-evaluation-item">
                      <span className="ReportCardTemplate-evaluation-trait">{item.trait}</span>
                      {isEditing ? (
                        <select
                          value={item.value.toString()}
                          onChange={(e) => handleEvaluationChange(index, e.target.value)}
                          className="ReportCardTemplate-evaluation-select"
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <div className="ReportCardTemplate-evaluation-indicator">
                          {item.value ? (
                            <FontAwesomeIcon icon={faCheck} className="ReportCardTemplate-evaluation-yes" />
                          ) : (
                            <FontAwesomeIcon icon={faTimesIcon} className="ReportCardTemplate-evaluation-no" />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="ReportCardTemplate-divider"></div>

              {/* Subjects Section */}
              <div className="ReportCardTemplate-subjects-section">
                <h2>SUBJECT</h2>
                <table className="ReportCardTemplate-subjects-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Grade</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataToUse.subjects.map(subject => (
                      <tr key={subject.id}>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              value={subject.name}
                              onChange={(e) => handleSubjectChange(subject.id, 'name', e.target.value)}
                              className="ReportCardTemplate-editInput"
                            />
                          ) : (
                            subject.name
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <select
                              value={subject.grade}
                              onChange={(e) => handleSubjectChange(subject.id, 'grade', e.target.value)}
                              className="ReportCardTemplate-grade-select"
                              style={{ 
                                backgroundColor: getGradeColor(subject.grade), 
                                color: 'white',
                                border: 'none'
                              }}
                            >
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                            </select>
                          ) : (
                            <span 
                              className="ReportCardTemplate-grade-badge"
                              style={{ backgroundColor: getGradeColor(subject.grade) }}
                            >
                              <FontAwesomeIcon 
                                icon={getGradeIcon(subject.grade)} 
                                className="ReportCardTemplate-grade-icon"
                              />
                              {subject.grade}
                            </span>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              value={subject.remarks}
                              onChange={(e) => handleSubjectChange(subject.id, 'remarks', e.target.value)}
                              className="ReportCardTemplate-editInput ReportCardTemplate-remarks-input"
                            />
                          ) : (
                            <span className="ReportCardTemplate-remarks">{subject.remarks}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Metrics and Remarks Section */}
              <div className="ReportCardTemplate-bottom-section">
                <div className="ReportCardTemplate-metrics-section">
                  <h3>METRICS:</h3>
                  <table className="ReportCardTemplate-metrics-table">
                    <thead>
                      <tr>
                        <th>Grade</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataToUse.metrics.map((metric, index) => (
                        <tr key={index}>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                value={metric.grade}
                                onChange={(e) => handleMetricChange(index, 'grade', e.target.value)}
                                className="ReportCardTemplate-editInput ReportCardTemplate-metric-grade"
                              />
                            ) : (
                              <span className="ReportCardTemplate-metric-grade">{metric.grade}</span>
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                value={metric.description}
                                onChange={(e) => handleMetricChange(index, 'description', e.target.value)}
                                className="ReportCardTemplate-editInput"
                              />
                            ) : (
                              metric.description
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="ReportCardTemplate-remarks-section">
                  <h3>REMARKS</h3>
                  {isEditing ? (
                    <textarea
                      value={dataToUse.overallRemarks}
                      onChange={(e) => handleInputChange('overallRemarks', e.target.value)}
                      className="ReportCardTemplate-editTextarea ReportCardTemplate-overall-remarks"
                      rows="4"
                    />
                  ) : (
                    <div className="ReportCardTemplate-overall-remarks-text">
                      {dataToUse.overallRemarks}
                    </div>
                  )}
                  
                  <div className="ReportCardTemplate-overall-grade">
                    <h3>GRADE</h3>
                    {isEditing ? (
                      <select
                        value={dataToUse.overallGrade}
                        onChange={(e) => handleInputChange('overallGrade', e.target.value)}
                        className="ReportCardTemplate-overall-grade-select"
                        style={{ 
                          backgroundColor: getGradeColor(dataToUse.overallGrade), 
                          color: 'white',
                          border: 'none'
                        }}
                      >
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    ) : (
                      <div 
                        className="ReportCardTemplate-final-grade"
                        style={{ backgroundColor: getGradeColor(dataToUse.overallGrade) }}
                      >
                        {dataToUse.overallGrade}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Signature Section */}
              <div className="ReportCardTemplate-signature-section-new">
                <div className="ReportCardTemplate-signature-item">
                  <div className="ReportCardTemplate-signature-line"></div>
                  <span>Teacher's Signature</span>
                </div>
                <div className="ReportCardTemplate-signature-item">
                  <div className="ReportCardTemplate-signature-line"></div>
                  <span>Principal's Signature</span>
                </div>
                <div className="ReportCardTemplate-signature-item">
                  <div className="ReportCardTemplate-signature-line"></div>
                  <span>Date</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="ReportCardTemplate-actionSection">
            <button className="ReportCardTemplate-btn ReportCardTemplate-btnSecondary" onClick={() => window.print()}>
              <FontAwesomeIcon icon={faPrint} /> Print Report Card
            </button>
            <button className="ReportCardTemplate-btn ReportCardTemplate-btnPrimary" onClick={generatePDF}>
              <FontAwesomeIcon icon={faDownload} /> Download as PDF
            </button>
            {isEditing && (
              <button 
                className="ReportCardTemplate-btn ReportCardTemplate-btnCancel"
                onClick={() => {
                  setIsEditing(false);
                  setEditedData(reportCardData);
                }}
              >
                <FontAwesomeIcon icon={faTimesCircle} /> Cancel
              </button>
            )}
          </div>

          {/* Students List */}
          <div className="ReportCardTemplate-studentsList">
            <div className="ReportCardTemplate-studentsHeader">
              <h3>
                <FontAwesomeIcon icon={faUsers} /> Students List
              </h3>
            </div>
            
            <div className="ReportCardTemplate-studentsTable">
              <table className="ReportCardTemplate-table ReportCardTemplate-tableStudents">
                <thead>
                  <tr>
                    <th>Roll No</th>
                    <th>Student Name</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map(student => (
                    <tr key={student.id}>
                      <td>{student.rollNumber}</td>
                      <td>
                        <strong>{student.name}</strong>
                      </td>
                      <td>{student.class}</td>
                      <td>{student.section}</td>
                      <td>
                        <button 
                          className="ReportCardTemplate-actionBtn ReportCardTemplate-viewBtn"
                          onClick={() => setSelectedStudent(student)}
                        >
                          <FontAwesomeIcon icon={faEye} /> View Report
                        </button>
                        <button 
                          className="ReportCardTemplate-actionBtn ReportCardTemplate-generateBtn"
                          onClick={() => {
                            setReportCardData(prev => ({
                              ...prev,
                              studentName: student.name,
                              studentName: student.name,
                              school: "BRIDGE HIGH SCHOOL",
                              teacherName: "Ms. Jennifer Wilson",
                              sessions: "Fall 2024"
                            }));
                            Swal.fire({
                              title: "Loaded!",
                              text: `Now viewing ${student.name}'s report card`,
                              icon: "success",
                              timer: 1500,
                              showConfirmButton: false
                            });
                          }}
                        >
                          <FontAwesomeIcon icon={faFileSignature} /> Load Template
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {students.length === 0 && (
                <div className="ReportCardTemplate-noData">
                  <FontAwesomeIcon icon={faSearch} size="3x" />
                  <h3>No students found</h3>
                  <p>Try adjusting your search criteria</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReportCardTemplate;